import { Inject, Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import * as _ from 'lodash';
import { DOCUMENT } from '@angular/common';
import { Config } from '../models/configuration.model';
import { NavigatorService } from './navigator.service';

export interface IWindow extends Window {
  webkitSpeechRecognition: any;
}

@Injectable()
export class VoiceControlService {

  recognition: any = null;
  recognizing = false;
  first = true;
  supported = false;

  utter = new SpeechSynthesisUtterance();
  synth = window.speechSynthesis;

  updateSpeechOutput: Subject<any> = new Subject();
  link: Subject<any> = new Subject();

  updateModality: Subject<any> = new Subject();
  changeTheme: Subject<any> = new Subject();
  selectPublications: Subject<any> = new Subject();
  sortPublications: Subject<any> = new Subject();
  searchInput: Subject<any> = new Subject();
  closeSearch: Subject<any> = new Subject();
  showNavigationMenu: Subject<any> = new Subject();
  openUrl: Subject<any> = new Subject();
  changeSortDirectionPublicationList: Subject<any> = new Subject();

  clearInputField: any;
  clearOutputField: any;

  speechHistory = '';

  config: Config;

  responses_misunderstood = ["Could you repeat that?", "Try again!", "Perhaps you could rephrase that?", "I'm afraid I misunderstood what you were trying to say"];
  responses_unknown = ["I'm not sure what you mean?", "I'm sorry, I only have a small vocabulary.", "Sorry, you need to help me out here."];
  responses_welcome = ["Hi, what can I help you with?", "Hello, how are you today?", "Hi, nice to see you!", "Welcome, how can I assist?"];
  responses_thanks = ["You're welcome", "My pleasure", "Is there anything else I can assist you with?"];
  responses_goodbye = ["Goodbye", "Thanks for your visit!", "Hope to see you again!"];

  public commonSearchWords = ['the', 'it is', 'a', 'an', 'by', 'to', 'get', 'you', 'me', 'your', 'from', 'and', 'show', 'can', 'all', 'could', 'how', 'in', 'with', 'about', 'but', 'at', 'on', 'it', 'i', 'are', 'to', 'for', 'of', 'paper', 'papers', 'publication', 'publications', 'search', 'find', 'select', 'feedback', 'interaction'];


  constructor(@Inject(DOCUMENT) private document: Document, private navigatorService: NavigatorService) { }


  public navigateTo(page: string) {
    this.link.next(page);
  }

  public updateTheme(theme: string) {
    this.changeTheme.next(theme);
  }

  public updateInteractionModality(modality: string, state: boolean) {
    this.updateModality.next({ modality: modality, state: state });
  }

  public updatePublicationSelection(type: string) {
    this.selectPublications.next(type);
  }

  public sortPublicationsBy(type: string) {
    this.sortPublications.next(type);
  }

  public search(querry: string) {
    this.searchInput.next(querry);
  }

  genericMessage() {
    const responseMsg_index = this.getRandomInt(this.responses_welcome.length);
    this.respond(this.responses_welcome[responseMsg_index]);
  }

  respondToUnknownQuery(e: any) {
    if (e.results[e.results.length - 1][0].confidence < 0.6) {
      const responseMsg_index = this.getRandomInt(this.responses_misunderstood.length);
      this.respond(this.responses_misunderstood[responseMsg_index]);
    } else {
      const responseMsg_index = this.getRandomInt(this.responses_unknown.length);
      this.respond(this.responses_unknown[responseMsg_index]);
    }
  }


  getRandomInt(max: number) {
    return Math.floor(Math.random() * max);
  }




  initializeSpeechRecognition() {

    if ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window) {
      this.supported = true;

      this.utter.onend = () => {
        if (!this.recognizing) {
          this.recognition.start();
        }
      }

      const { webkitSpeechRecognition } = (window as any);
      const { SpeechRecognition } = (window as any);
      this.recognition = new (webkitSpeechRecognition || SpeechRecognition)();
      this.recognition.continuous = true;
      this.recognition.lang = 'en-GB';
      this.recognition.interimResults = false;
      this.recognition.maxAlternatives = 2;



      this.recognition.onstart = () => {
        this.recognizing = true;
        this.navigatorService.startDetecting();
      };
      this.recognition.onend = () => {
        this.recognizing = false;
        this.navigatorService.stopDetecting();
      };
      this.recognition.onerror = () => {
        this.recognizing = false;
        this.navigatorService.stopDetecting();
      };
      this.recognition.onresult = (e: any) => {
        let interim_transcript = '';

        interim_transcript = e.results[e.results.length - 1][0].transcript.trim().toLowerCase();
        interim_transcript = interim_transcript.replace(/\./g, '').replace(/\?/g, '');

        (this.document.getElementById('speech-output') as HTMLElement).innerHTML = '';
        (this.document.getElementById('speech-input') as HTMLElement).innerHTML = interim_transcript;

        if (interim_transcript.includes('contact') || interim_transcript.includes('details') || interim_transcript.includes('address')) {
          this.respond('these are my contact details');
          this.navigateTo('contact');
        }
        // page navigation
        else if (interim_transcript.includes('teach') || interim_transcript.includes('teaching') || interim_transcript.includes('course')) {
          this.respond('these are the courses I teach');
          this.navigateTo('teaching');
        }
        else if (this.config.activePage !== 'publications' && (interim_transcript.includes('publication') || interim_transcript.includes('publications'))) {
          this.respond('these are my publications');
          this.navigateTo('publications');
        }
        else if (interim_transcript.includes('home') || interim_transcript.includes('welcome') || interim_transcript.includes('main page')) {
          this.respond('here is the welcome page');
          this.navigateTo('/');
        }




        // activate modalities or change dark/light mode
        else if (this.speechHistory === 'activate' || (interim_transcript.includes('turn') && interim_transcript.includes(' on')) || interim_transcript.includes('activate') || interim_transcript.includes('enable')) {
          this.speechHistory = '';
          if (interim_transcript.includes('audio') || interim_transcript.includes('sound')) {
            this.updateInteractionModality('audio', true);
            this.respond('Audio feedback is turned on');
          }
          else if (interim_transcript.includes('haptic') || interim_transcript.includes('touch')) {
            this.respond('Haptic feedback is turned on');
            this.updateInteractionModality('haptic', true);
          }
          else if (interim_transcript.includes('visual') || interim_transcript.includes('vision')) {
            this.respond('Visual feedback is turned on');
            this.updateInteractionModality('vision', true);
          }
          else if (interim_transcript.includes('dark') || interim_transcript.includes('night')) {
            this.respond('Dark mode is turned on');
            this.updateTheme('dark-mode');
          }
          else if (interim_transcript.includes('light')) {
            this.respond('Light mode is turned on');
            this.updateTheme('light-mode');
          }
          else {
            this.respond("Sorry, what would you like me to activate?");
            this.speechHistory = 'activate';
          }
        }

        // disable modalities or change dark/light mode
        else if (this.speechHistory === 'disable' || interim_transcript.includes('disable') || interim_transcript.includes('deactivate') || (interim_transcript.includes('turn') && interim_transcript.includes('off'))) {
          this.speechHistory = '';
          if (interim_transcript.includes('audio') || interim_transcript.includes('sound')) {
            this.updateInteractionModality('audio', false);
            this.respond('audio feedback is turned off');
          }
          else if (interim_transcript.includes('haptic') || interim_transcript.includes('touch')) {
            this.respond('haptic feedback is turned off');
            this.updateInteractionModality('haptic', false);
          }
          else if (interim_transcript.includes('voice') || interim_transcript.includes('speech')) {
            this.respond('voice control is turned off');
            this.updateInteractionModality('speech', false);
          }
          else if (interim_transcript.includes('visual') || interim_transcript.includes('vision')) {
            this.respond('visual feedback is turned off');
            this.updateInteractionModality('vision', false);
          }
          else if (interim_transcript.includes('dark') || interim_transcript.includes('dark mode') || interim_transcript.includes('night mode')) {
            this.respond('Dark mode is turned off');
            this.updateTheme('light-mode');
          }
          else if (interim_transcript.includes('light') || interim_transcript.includes('light mode')) {
            this.respond('Light mode is turned off');
            this.updateTheme('dark-mode');
          } else {
            this.respond("Sorry, what would you like to disable?");
            this.speechHistory = 'disable';
          }
        }


        // specific controls
        else if (interim_transcript.includes('dark') || interim_transcript.includes('dark mode') || interim_transcript.includes('night mode')) {
          this.respond('Dark mode is turned on');
          this.updateTheme('dark-mode');
        }
        else if (interim_transcript.includes('light') || interim_transcript.includes('light mode')) {
          this.respond('Light mode is turned on');
          this.updateTheme('light-mode');
        }
        else if (interim_transcript.includes('menu') || interim_transcript.includes('navigation')) {
          this.respond('Here you go');
          this.showNavigationMenu.next();
        }
        else if (interim_transcript.includes('linkedin')) {
          this.respond('Here you go');
          this.openUrl.next('linkedin');
        }
        else if (interim_transcript.includes('twitter')) {
          this.respond('Here you go');
          this.openUrl.next('twitter');
        }
        else if (interim_transcript.includes('email') || interim_transcript.includes('mail')) {
          this.respond('Here you go');
          this.openUrl.next('mail');
        }



        //common responses
        else if ((interim_transcript.includes('what ') || interim_transcript.includes('how ') || interim_transcript.includes('help')) && (interim_transcript.includes('could') || interim_transcript.includes('can'))) {
          this.respond('You can ask me to navigate, search publications, change interaction modalities, and the like.');
        }
        else if (interim_transcript.includes('hello') || interim_transcript === 'hi') {
          this.respond("hi!");
        }
        else if (interim_transcript.includes('how are you')) {
          this.respond("Great, thanks!");
        }
        else if (interim_transcript.includes('goodbye') || interim_transcript.includes('see you')  || interim_transcript.includes('bye')) {
          const responseMsg_index = this.getRandomInt(this.responses_goodbye.length - 1);
          this.respond(this.responses_goodbye[responseMsg_index]);
        }
        else if (interim_transcript.includes('good') || interim_transcript.includes('fine') || interim_transcript.includes('great')) {
          this.respond('awesome');
        }
        else if (interim_transcript.includes('thank you') || interim_transcript.includes('thanks')) {
          const responseMsg_index = this.getRandomInt(this.responses_thanks.length - 1);
          this.respond(this.responses_thanks[responseMsg_index]);
        }
        else if ((interim_transcript.includes('not') || interim_transcript.includes("wasn't") ) && interim_transcript.includes('talking') && interim_transcript.includes('you')) {
          this.respond('my apologies');
        }
        else if (interim_transcript.includes('awesome')) {
          this.respond("indeed!");
        }
        else if (interim_transcript === 'yes' || interim_transcript === 'no' || interim_transcript === 'nothing') {
          this.speechHistory = '';
          this.respond("Okay");
        }
        else if (interim_transcript === 'ok' || interim_transcript === 'okay' || interim_transcript === 'nevermind' || interim_transcript.includes('no worries') || (interim_transcript.includes('never') && interim_transcript.includes('mind'))) {
          this.speechHistory = '';
          this.respond("Okay");
        }
        else if (interim_transcript === 'sure') {
          this.respond("Thanks");
        }
        else if (interim_transcript === 'shut up') {
          if (this.config.modalities[2].active) {
            this.updateInteractionModality('audio', false);
            this.respond('Okay, I turned off all audio feedback.');
          } else {
            this.updateInteractionModality('speech', false);
          }
        }
        else if (interim_transcript.includes('***')) {
          this.respond("If you dislike the voice interface, you can always turn it off.");
        }

        // respond to search queries

        else if (this.config.activePage === 'publications') {
          if (interim_transcript.includes('search') || interim_transcript.includes('find') || interim_transcript.includes('select') || interim_transcript.includes('get') || interim_transcript.includes('show')) {
            const uncommonWords = this.getUncommon(interim_transcript, this.commonSearchWords);
            if (interim_transcript.includes('search') && (interim_transcript.includes('close') || interim_transcript.includes('cancel') || interim_transcript.includes('stop'))) {
              this.closeSearch.next();
            } else if (interim_transcript === 'show all publications' || interim_transcript === 'show all papers') {
              this.respond('this is a complete overview of my publications');
              this.updatePublicationSelection('all');
            } else if (uncommonWords.length > 0) {
              this.search(uncommonWords.join(' '));
            } else {
              this.respondToUnknownQuery(e);
            }
          }

          else if (interim_transcript.includes('sort')) {
            if (interim_transcript.includes('date')) {
              this.sortPublicationsBy('date');
              this.respond('the items are sorted by date');

            } else if (interim_transcript.includes('title')) {
              this.sortPublicationsBy('title');
              this.respond('the items are sorted by publication title');

            } else if (interim_transcript.includes('citations')) {
              this.sortPublicationsBy('citations');
              this.respond('the items are sorted by citations');

            } else {
              this.respondToUnknownQuery(e);
            }
          }

          else if ((interim_transcript.includes('direction') || interim_transcript.includes('order')) && (interim_transcript.includes('change') || interim_transcript.includes('reverse'))) {
            this.changeSortDirectionPublicationList.next();
            this.respond('here you go');
          }

          else if (interim_transcript.includes('conference papers')) {
            this.respond('these are all the conference papers');
            this.updatePublicationSelection('conference paper');

          } else if (interim_transcript.includes('book chapters') || interim_transcript.includes('journal articles') ) {
            this.respond('these are all the book chapters and journal articles');
            this.updatePublicationSelection('journal');

          } else if (interim_transcript.includes('magazine')) {
            this.respond('these are all the magazine articles');
            this.updatePublicationSelection('magazine articles');

          } else if (interim_transcript.includes('book chapters') || interim_transcript.includes('journal articles') ) {
            this.respond('these are all the book chapters and journal articles');
            this.updatePublicationSelection('journal');

          } else if (interim_transcript.includes('workshops') || interim_transcript.includes('workshop') ) {
            this.respond('these are all the workshop papers');
            this.updatePublicationSelection('workshop');

          } else if (interim_transcript.includes('thesis') || interim_transcript.includes('dissertation') ) {
            this.respond('this is my thesis');
            this.updatePublicationSelection('thesis');
          }

          else if (interim_transcript.includes('all publications') || interim_transcript.includes('all papers')) {
            this.respond('this is a complete overview of my publications');
            this.updatePublicationSelection('all');
          }
          else {
            this.respondToUnknownQuery(e);
          }
        }


        else if (this.config.activePage === 'welcome') {
          if (interim_transcript.includes('scroll') || interim_transcript.includes('project') ) {
            if (interim_transcript.includes('down') || interim_transcript.includes('project')) {
              (this.document.getElementById('page-wrapper') as HTMLElement).scrollTop = window.innerHeight;
              this.respond('here you go');
            } else if (interim_transcript.includes('up')) {
              window.scrollTo(0, 0);
              this.respond('here you go');
            }
          } else {
            this.respondToUnknownQuery(e);
          }
        }

        else if (interim_transcript !== '') {
          this.respondToUnknownQuery(e);
        }
      }

      this.recognition.start();
      this.genericMessage();
      this.navigatorService.initializeAudioDetection();

    } else {
      this.supported = false;
      this.noBrowserSupportMSG();

      setTimeout(() => {
        this.updateInteractionModality('speech', false);
      }, 3000);
    }
  }

  noBrowserSupportMSG() {
    this.navigatorService.showMessage('Sorry, your browser does not support speech recognition.');
  }

  restartSpeechRecognition() {
    if (!this.recognizing && this.recognition) {
      this.recognition.start();
    }
  }

  respond(text: string) {
    if (this.recognition) {
      if (this.config.modalities[2].active) {
        this.recognition.stop();
        this.utter.text = text;
        this.synth.speak(this.utter);
      }
      (this.document.getElementById('speech-output') as HTMLElement).innerHTML = text;

      clearTimeout(this.clearInputField);

      this.clearInputField = setTimeout(() => {
        (this.document.getElementById('speech-input') as HTMLElement).innerHTML = '';
      }, 5000);

      clearTimeout(this.clearOutputField);

      this.clearOutputField = setTimeout(() => {
        (this.document.getElementById('speech-output') as HTMLElement).innerHTML = '';
      }, 5000);
    }
  }

  getUncommon(sentence: string, common: Array<string>) {
    let wordArr = sentence.match(/\w+/g);
    let commonObj = {};
    let uncommonArr = [];
    let word: string;
    let i: number;

    for ( i = 0; i < common.length; i++ ) {
        commonObj[ common[i].trim() ] = true;
    }

    for ( i = 0; i < wordArr.length; i++ ) {
        word = wordArr[i].trim().toLowerCase();
        if ( !commonObj[word] ) {
            uncommonArr.push(word);
        }
    }
    return uncommonArr;
}


  disableSpeechRecognition() {
    if (this.supported) {
      this.recognition.abort();
    }
    this.recognizing = false;
    clearTimeout(this.clearInputField);
    clearTimeout(this.clearOutputField);
    this.navigatorService.disconnectAudioListener();
  }


  activateSpeechRecognition() {
    if (this.supported) {
      if (!this.recognizing) {
        this.recognition.start();
      } else {
        this.recognition.stop();
      }
    } else {
      this.noBrowserSupportMSG();
    }
  }

}

